import React from 'react';
import { Sparkles, Bot, ArrowRight, Database, ShieldCheck, Cpu } from 'lucide-react';
import { Link } from 'react-router-dom';

const AiTeaser = () => {
  return (
    <section className="py-20 bg-gradient-to-b from-white via-slate-50 to-white relative overflow-hidden">
      <div className="container mx-auto px-6 max-w-7xl">
        <div className="relative rounded-3xl bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900 text-white p-8 md:p-14 overflow-hidden shadow-2xl border border-slate-800">
          {/* Subtle background glow */}
          <div className="absolute -top-24 -right-24 w-96 h-96 bg-blue-600/20 rounded-full blur-3xl pointer-events-none" />
          <div className="absolute -bottom-24 -left-24 w-96 h-96 bg-purple-600/20 rounded-full blur-3xl pointer-events-none" />

          <div className="relative z-10 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            {/* Left Content */}
            <div className="lg:col-span-7 space-y-6">
              <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-blue-500/10 border border-blue-400/30 text-blue-300 text-xs font-semibold uppercase tracking-wider">
                <Sparkles size={14} className="text-blue-400" />
                Next-Gen RAG Architecture
              </div>

              <h2 className="text-3xl md:text-5xl font-extrabold tracking-tight leading-tight">
                Enterprise AI Grounded with{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 via-indigo-300 to-purple-400">
                  Real-Time Vector Knowledge
                </span>
              </h2>

              <p className="text-slate-300 text-base md:text-lg leading-relaxed max-w-xl">
                Experience high-precision conversational retrieval. Businex harnesses Google Gemini, ChromaDB vector indexing, and Microsoft SQL Server to deliver audit-ready enterprise AI solutions.
              </p>

              <div className="grid grid-cols-3 gap-4 pt-2">
                <div className="p-3 bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10">
                  <Cpu size={20} className="text-blue-400 mb-1.5" />
                  <div className="font-bold text-sm">Gemini AI</div>
                  <div className="text-[11px] text-slate-400">Contextual Reasoning</div>
                </div>
                <div className="p-3 bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10">
                  <Database size={20} className="text-purple-400 mb-1.5" />
                  <div className="font-bold text-sm">ChromaDB</div>
                  <div className="text-[11px] text-slate-400">Vector Embeddings</div>
                </div>
                <div className="p-3 bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10">
                  <ShieldCheck size={20} className="text-emerald-400 mb-1.5" />
                  <div className="font-bold text-sm">MSSQL</div>
                  <div className="text-[11px] text-slate-400">Session Audit Memory</div>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-4 pt-4">
                <Link to="/ai-assistant">
                  <button className="flex items-center gap-2 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white px-7 py-3.5 rounded-full font-semibold transition-all shadow-lg hover:shadow-blue-500/30 text-sm">
                    Open AI Studio
                    <ArrowRight size={16} />
                  </button>
                </Link>
                <span className="text-xs text-slate-400">
                  Or click <span className="text-blue-400 font-semibold">"Ask Businex AI"</span> in the bottom corner
                </span>
              </div>
            </div>

            {/* Right Interactive Mockup Card */}
            <div className="lg:col-span-5">
              <div className="bg-slate-900/90 rounded-2xl border border-slate-700/80 p-5 shadow-xl space-y-3 font-sans">
                <div className="flex items-center justify-between pb-3 border-b border-slate-800 text-xs">
                  <div className="flex items-center gap-2">
                    <Bot size={16} className="text-blue-400" />
                    <span className="font-semibold text-slate-200">Businex RAG Assistant</span>
                  </div>
                  <span className="flex items-center gap-1.5 text-[11px] text-emerald-400 font-mono">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"></span>
                    API: localhost:3000
                  </span>
                </div>

                {/* Simulated User Bubble */}
                <div className="flex justify-end">
                  <div className="bg-blue-600 text-white text-xs px-3.5 py-2 rounded-2xl rounded-br-xs max-w-[85%]">
                    What are your business operating hours?
                  </div>
                </div>

                {/* Simulated Bot Bubble */}
                <div className="flex justify-start">
                  <div className="bg-slate-800 text-slate-200 text-xs p-3 rounded-2xl rounded-bl-xs border border-slate-700/60 max-w-[90%] space-y-2">
                    <p className="leading-relaxed">
                      Businex Enterprise Platform operates Monday through Friday, from 8:00 AM to 8:00 PM EST. For Enterprise Tier subscribers, emergency critical support is available 24/7/365.
                    </p>
                    <div className="pt-2 border-t border-slate-700 flex items-center justify-between text-[10px] text-slate-400">
                      <span className="text-blue-300 font-mono">📚 Source: kb-001 (business_hours)</span>
                      <span className="text-emerald-400 font-mono">dist: 0.124</span>
                    </div>
                  </div>
                </div>

                <div className="pt-2 text-center">
                  <Link
                    to="/ai-assistant"
                    className="text-xs text-blue-400 hover:text-blue-300 font-semibold inline-flex items-center gap-1"
                  >
                    Test with your own queries &rarr;
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AiTeaser;
